import { useAccountingCompany } from '../components/HoldingAccounting';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function FinancialDuePayments() {
  const companyId = useAccountingCompany();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const base = `${API}/companies/${companyId}/finance`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [tab, setTab] = useState('incoming');
  const [showFollowUp, setShowFollowUp] = useState(null);
  const [followUpForm, setFollowUpForm] = useState({ followable_type: '', followable_id: '', type: 'reminder', notes: '', next_follow_up_date: '', promise_date: '', promise_amount: '' });

  const fetchData = () => {
    setLoading(true);
    fetch(`${base}/due-payments`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : {})
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (companyId) fetchData(); }, [companyId]);

  const saveFollowUp = async () => {
    const res = await fetch(`${base}/due-payments/follow-up`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(followUpForm)
    });
    if (res.ok) { setShowFollowUp(null); fetchData(); }
  };

  const openFollowUp = (item, type) => {
    setFollowUpForm({
      followable_type: type,
      followable_id: item.id,
      type: 'reminder',
      notes: '',
      next_follow_up_date: '',
      promise_date: '',
      promise_amount: ''
    });
    setShowFollowUp(item);
  };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const btnStyle = { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };
  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', boxSizing: 'border-box' };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };

  if (initialLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  const incoming = data?.incoming || [];
  const outgoing = data?.outgoing || [];
  const forecast = data?.cash_flow_forecast || {};

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 24px', fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>📅 Due & Upcoming Payments</h2>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ ...sectionStyle, borderLeft: '4px solid #22c55e', marginBottom: 0 }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Expected Incoming</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22c55e' }}>{fmt(data?.incoming_summary?.total)}</div>
          <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>Overdue: {fmt(data?.incoming_summary?.overdue)}</div>
        </div>
        <div style={{ ...sectionStyle, borderLeft: '4px solid #ef4444', marginBottom: 0 }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Expected Outgoing</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(data?.outgoing_summary?.total)}</div>
          <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>Overdue: {fmt(data?.outgoing_summary?.overdue)}</div>
        </div>
        <div style={{ ...sectionStyle, borderLeft: '4px solid #3b82f6', marginBottom: 0 }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Forecast (30 days)</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(forecast.expected_inflow_30d)}</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>Out: {fmt(forecast.expected_outflow_30d)}</div>
        </div>
        <div style={{ ...sectionStyle, borderLeft: '4px solid #8b5cf6', marginBottom: 0 }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Net Forecast (30d)</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 700, color: (forecast.expected_inflow_30d || 0) - (forecast.expected_outflow_30d || 0) >= 0 ? '#22c55e' : '#ef4444' }}>{fmt((forecast.expected_inflow_30d || 0) - (forecast.expected_outflow_30d || 0))}</div>
        </div>
      </div>

      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTab('incoming')} style={{ ...btnStyle, background: tab === 'incoming' ? '#22c55e' : '#f1f5f9', color: tab === 'incoming' ? '#fff' : '#374151' }}>📥 Incoming Due ({incoming.length})</button>
        <button onClick={() => setTab('outgoing')} style={{ ...btnStyle, background: tab === 'outgoing' ? '#ef4444' : '#f1f5f9', color: tab === 'outgoing' ? '#fff' : '#374151' }}>📤 Outgoing Due ({outgoing.length})</button>
      </div>

      {/* Incoming Due */}
      {tab === 'incoming' && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📥 Incoming Due Payments</h3>
          {incoming.length === 0 ? <div style={{ color: '#94a3b8' }}>No due incoming payments</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr><th style={thStyle}>Ref</th><th style={thStyle}>Payer</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount Due</th><th style={thStyle}>Due Date</th><th style={thStyle}>Days</th><th style={thStyle}>Actions</th></tr>
                </thead>
                <tbody>
                  {incoming.map(r => {
                    const daysUntil = r.days_until_due || 0;
                    const isOverdue = daysUntil < 0;
                    return (
                      <tr key={r.id} style={{ background: isOverdue ? '#fef2f2' : daysUntil <= 3 ? '#fefce8' : 'transparent' }}>
                        <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6366f1' }}>{r.reference}</span></td>
                        <td style={tdStyle}>{r.payer?.name || r.payer || '-'}</td>
                        <td style={tdStyle}>{r.project?.name || r.project || '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(r.remaining_amount || r.amount)}</td>
                        <td style={tdStyle}>{r.due_date ? new Date(r.due_date).toLocaleDateString() : '-'}</td>
                        <td style={tdStyle}><span style={{ fontWeight: 600, color: isOverdue ? '#ef4444' : daysUntil <= 7 ? '#f59e0b' : '#22c55e' }}>{isOverdue ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d`}</span></td>
                        <td style={tdStyle}><button onClick={() => openFollowUp(r, 'App\\Models\\FinReceivable')} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>📞 Follow-up</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Outgoing Due */}
      {tab === 'outgoing' && (
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📤 Outgoing Due Payments</h3>
          {outgoing.length === 0 ? <div style={{ color: '#94a3b8' }}>No due outgoing payments</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr><th style={thStyle}>Ref</th><th style={thStyle}>Payee</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount Due</th><th style={thStyle}>Due Date</th><th style={thStyle}>Days</th><th style={thStyle}>Actions</th></tr>
                </thead>
                <tbody>
                  {outgoing.map(p => {
                    const daysUntil = p.days_until_due || 0;
                    const isOverdue = daysUntil < 0;
                    return (
                      <tr key={p.id} style={{ background: isOverdue ? '#fef2f2' : daysUntil <= 3 ? '#fefce8' : 'transparent' }}>
                        <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6366f1' }}>{p.reference}</span></td>
                        <td style={tdStyle}>{p.payee?.name || p.payee || '-'}</td>
                        <td style={tdStyle}>{p.project?.name || p.project || '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(p.remaining_amount || p.amount)}</td>
                        <td style={tdStyle}>{p.due_date ? new Date(p.due_date).toLocaleDateString() : '-'}</td>
                        <td style={tdStyle}><span style={{ fontWeight: 600, color: isOverdue ? '#ef4444' : daysUntil <= 7 ? '#f59e0b' : '#22c55e' }}>{isOverdue ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d`}</span></td>
                        <td style={tdStyle}><button onClick={() => openFollowUp(p, 'App\\Models\\FinPayable')} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>📝 Note</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowUp && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '90%', maxWidth: 480 }}>
            <h3 style={{ margin: '0 0 20px' }}>Follow-up / Reminder</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Type</label><select value={followUpForm.type} onChange={e => setFollowUpForm({ ...followUpForm, type: e.target.value })} style={inputStyle}><option value="reminder">Reminder</option><option value="phone_call">Phone Call</option><option value="email">Email</option><option value="promise_to_pay">Promise to Pay</option><option value="note">Note</option></select></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Notes</label><textarea rows={3} value={followUpForm.notes} onChange={e => setFollowUpForm({ ...followUpForm, notes: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Next Follow-up Date</label><input type="date" value={followUpForm.next_follow_up_date} onChange={e => setFollowUpForm({ ...followUpForm, next_follow_up_date: e.target.value })} style={inputStyle} /></div>
              {followUpForm.type === 'promise_to_pay' && (
                <>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Promise Date</label><input type="date" value={followUpForm.promise_date} onChange={e => setFollowUpForm({ ...followUpForm, promise_date: e.target.value })} style={inputStyle} /></div>
                  <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Promise Amount</label><input type="number" step="0.01" value={followUpForm.promise_amount} onChange={e => setFollowUpForm({ ...followUpForm, promise_amount: e.target.value })} style={inputStyle} /></div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowFollowUp(null)} style={{ ...btnStyle, background: '#e2e8f0' }}>Cancel</button>
              <button onClick={saveFollowUp} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
